import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit";
import * as api from "../service/pagesService";
import type { CmsPage, PageFormData, PaginatedPages, PageStatus } from "../types";

interface PagesState {
  pages: CmsPage[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  loading: boolean;
  error: string | null;
}

const initialState: PagesState = { pages: [], page: 1, limit: 20, total: 0, totalPages: 1, loading: false, error: null };

export const fetchPages = createAsyncThunk("cms/pages/fetch", (page: number = 1) => api.listAdminPages(page));
export const addPage = createAsyncThunk("cms/pages/add", (payload: PageFormData) => api.createPage(payload));
export const editPage = createAsyncThunk("cms/pages/edit", ({ id, payload }: { id: number; payload: PageFormData }) => api.updatePage(id, payload));
export const changePageStatus = createAsyncThunk("cms/pages/setStatus", ({ id, status }: { id: number; status: PageStatus }) => api.setPageStatus(id, status));
export const removePage = createAsyncThunk("cms/pages/remove", async (id: number) => {
  await api.deletePage(id);
  return id;
});

const replace = (state: PagesState, action: PayloadAction<CmsPage>) => {
  const idx = state.pages.findIndex((p) => p.id === action.payload.id);
  if (idx !== -1) state.pages[idx] = action.payload;
};

const pagesSlice = createSlice({
  name: "cmsPages",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPages.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchPages.fulfilled, (state, action: PayloadAction<PaginatedPages>) => {
        state.loading = false;
        state.pages = action.payload.pages;
        state.page = action.payload.pagination.page;
        state.limit = action.payload.pagination.limit;
        state.total = action.payload.pagination.total;
        state.totalPages = action.payload.pagination.totalPages;
      })
      .addCase(fetchPages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "Failed to load pages.";
      })
      .addCase(editPage.fulfilled, replace)
      .addCase(changePageStatus.fulfilled, replace)
      .addCase(removePage.fulfilled, (state, action: PayloadAction<number>) => {
        state.pages = state.pages.filter((p) => p.id !== action.payload);
      });
  },
});

export default pagesSlice.reducer;
